/**
 * Case detail (§08).
 *
 * Everything a moderator needs to decide, on one screen, in the order they
 * actually need it: what was reported, who it is about, what they have been
 * saying, and then the five verdicts. The note is required — it is the record of
 * why, it is what the next moderator reads when the same account comes back, and
 * it is what a grievance officer answers with under the IT Rules (§09).
 *
 * Photos arrive blurred and stay blurred until the moderator asks. Half of what
 * lands in this queue is an NCII or minor-signal report; nobody should have that
 * on screen because a page loaded.
 */
import { useCallback, useEffect, useState } from 'react';
import { MODERATION_VERDICTS } from '@alap/shared';
import type { ModerationVerdict } from '@alap/shared';
import { ApiError, getCase, postVerdict } from '../api';
import type { CaseDetailResponse, CaseMessage, CasePhoto, VerdictResponse } from '../api';
import {
  AccountChip,
  AdvisoryBlock,
  Countdown,
  ErrorState,
  SEVERITY_LABEL,
  SafetyScoreChips,
  SeverityStripe,
  Skeletons,
  clusterName,
  formatTimestamp,
  severityFor,
  shortId,
  useNow,
} from './kit';

/** What each verdict actually does, in the words of §08. */
const VERDICT_COPY: Record<ModerationVerdict, { label: string; what: string; weight: number }> = {
  no_action: {
    label: 'No action',
    what: 'The report does not breach policy. The case closes and nothing happens to the account.',
    weight: 1,
  },
  warned: {
    label: 'Warned',
    what: 'The account stays and a warning goes on record. Repeat reports escalate from here.',
    weight: 2,
  },
  content_removed: {
    label: 'Content removed',
    what: 'Reported messages are redacted and offending photos pulled. The account survives.',
    weight: 3,
  },
  suspended: {
    label: 'Suspended',
    what: 'The account is suspended and the profile hidden. Use this while an investigation runs, or pending re-verification.',
    weight: 4,
  },
  banned: {
    label: 'Banned',
    what: 'Permanent. Ban keys are written for device fingerprint, phone hash and every payment instrument on record, not just the account (§08).',
    weight: 5,
  },
};

const MIN_NOTE = 8;

export default function CaseDetail({ caseId, onBack }: { caseId: string; onBack: () => void }) {
  const [data, setData] = useState<CaseDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const now = useNow(1000);

  const [verdict, setVerdict] = useState<ModerationVerdict | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<VerdictResponse | null>(null);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      setData(await getCase(id));
    } catch (err) {
      setData(null);
      setError(err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setVerdict(null);
    setNote('');
    setResult(null);
    setSubmitError(null);
    void load(caseId);
  }, [caseId, load]);

  async function submit() {
    if (!verdict || note.trim().length < MIN_NOTE || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await postVerdict({ caseId, verdict, note: note.trim() });
      setResult(res);
      setVerdict(null);
      setNote('');
      await load(caseId);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err)));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading && !data) {
    return (
      <>
        <BackLink onBack={onBack} />
        <Skeletons count={3} height={160} />
      </>
    );
  }

  if (error) {
    return (
      <>
        <BackLink onBack={onBack} />
        <ErrorState
          title={error.status === 404 ? 'That case is gone' : 'The case did not load'}
          message={
            error.status === 404
              ? 'No case with that id. It may have been closed and purged, or the link is stale — go back to the queue and pick it up from there.'
              : error.message
          }
          code={`${error.code}${error.status ? ` · HTTP ${error.status}` : ''}`}
          onRetry={() => void load(caseId)}
        />
      </>
    );
  }

  if (!data) return null;

  const k = data.case;
  const s = data.subject;
  const severity = severityFor(k.slaMins);
  const closed = k.status !== 'open';
  const subjectMessages = data.messages.filter((m) => m.from === 'subject');

  return (
    <>
      <BackLink onBack={onBack} />

      <header className={`case-header ${k.overdue ? 'is-overdue' : ''}`}>
        <div style={{ width: 10, alignSelf: 'stretch', display: 'flex' }}>
          <SeverityStripe severity={severity} />
        </div>

        <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="eyebrow">
            {SEVERITY_LABEL[severity]} · case {shortId(k.id)} · raised via {k.source.replace(/_/g, ' ')}
          </div>
          <h2>{k.reasonLabel}</h2>
          <div className="chip-row">
            <span className="chip chip-mono">{k.reason}</span>
            <span className={`chip ${closed ? 'chip-indigo' : 'chip-brass'}`}>{k.status}</span>
            {k.verdict && <span className="chip chip-alta">verdict: {k.verdict.replace(/_/g, ' ')}</span>}
            {k.evidenceHold && <span className="chip chip-alta">evidence preserved</span>}
            {k.policyAction.autoHide && <span className="chip chip-brass">auto-hidden on report</span>}
            {k.assignedTo && <span className="chip">assigned: {k.assignedTo}</span>}
          </div>
          {k.note && (
            <p style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              <span className="eyebrow">Closing note</span>
              <br />
              {k.note}
            </p>
          )}
        </div>

        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          {k.overdue && <span className="overdue-flag">overdue</span>}
          <Countdown dueAt={k.dueAt} now={now} slaLabel={k.slaLabel} live={!closed} />
          <div className="eyebrow" style={{ marginTop: 6 }}>
            opened {formatTimestamp(k.openedAt)}
            {k.closedAt ? ` · closed ${formatTimestamp(k.closedAt)}` : ''}
          </div>
        </div>
      </header>

      <div className="case-cols">
        {/* ---------------- left: who this is about ---------------- */}
        <div className="stack">
          <section className="card">
            <div className="card-head">
              <h3>Subject</h3>
              <div style={{ marginLeft: 'auto' }}>
                <AccountChip status={s.accountStatus} />
              </div>
            </div>
            <div className="card-body stack" style={{ gap: 'var(--s-md)' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px' }}>
                  {s.displayName}
                </div>
                <div className="eyebrow">
                  {shortId(s.userId)} · {clusterName(s.clusterId)}
                </div>
              </div>

              <dl className="kv">
                <dt>Age</dt>
                <dd className="num">{s.ageYears === null ? 'not on record' : `${s.ageYears}`}</dd>
                <dt>Gender</dt>
                <dd>{s.gender ?? '—'}{s.pronouns ? ` · ${s.pronouns}` : ''}</dd>
                <dt>Structure</dt>
                <dd>{s.structure ? s.structure.replace(/_/g, ' ') : '—'}</dd>
                <dt>Profile</dt>
                <dd>{s.hidden ? 'hidden from discovery' : 'visible'}</dd>
                <dt>Onboarding</dt>
                <dd>{s.onboardingStep}</dd>
                <dt>Joined</dt>
                <dd>{formatTimestamp(s.joinedAt)}</dd>
                <dt>Last seen</dt>
                <dd>{formatTimestamp(s.lastSeenAt)}</dd>
                {s.suspendedAt && (
                  <>
                    <dt>Suspended</dt>
                    <dd>{formatTimestamp(s.suspendedAt)}</dd>
                  </>
                )}
                {s.bannedAt && (
                  <>
                    <dt>Banned</dt>
                    <dd>
                      {formatTimestamp(s.bannedAt)}
                      {s.banReason ? ` — ${s.banReason}` : ''}
                    </dd>
                  </>
                )}
                <dt>Ban keys held</dt>
                <dd className="num">{s.bansOnRecord}</dd>
              </dl>

              <div>
                <div className="eyebrow" style={{ marginBottom: 6 }}>
                  Verifications (§08)
                </div>
                <div className="chip-row">
                  {s.verifications.length === 0 && (
                    <span className="chip chip-alta">none — not even liveness</span>
                  )}
                  {s.verifications.map((v) => (
                    <span
                      key={`${v.kind}-${v.grantedAt}`}
                      className={`chip ${v.revokedAt ? 'chip-alta' : 'chip-river'}`}
                    >
                      {v.kind.replace(/_/g, ' ')}
                      {v.revokedAt ? ' · revoked' : ''}
                    </span>
                  ))}
                </div>
              </div>

              {s.bio && (
                <div>
                  <div className="eyebrow" style={{ marginBottom: 4 }}>
                    Bio
                  </div>
                  <p style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{s.bio}</p>
                </div>
              )}

              {s.promptAnswers.length > 0 && (
                <div>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>
                    Prompt answers
                  </div>
                  <div className="stack" style={{ gap: 'var(--s-sm)' }}>
                    {s.promptAnswers.map((p) => (
                      <div key={p.id} className="report">
                        <span className="eyebrow">
                          {p.promptId}
                          {p.qualityScore !== null ? ` · quality ${p.qualityScore.toFixed(2)}` : ''}
                        </span>
                        <p>{p.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h3>Photos</h3>
              <span className="chip">{s.photos.length}</span>
              <span className="eyebrow" style={{ marginLeft: 'auto' }}>
                blurred until asked
              </span>
            </div>
            <div className="card-body">
              {s.photos.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>
                  No photos on this account. For a catfish or NCII report that is itself worth
                  noting in the verdict.
                </p>
              ) : (
                <div className="photo-grid">
                  {s.photos.map((p) => (
                    <Photo key={p.id} photo={p} />
                  ))}
                </div>
              )}
            </div>
          </section>

          {data.priorCases.length > 0 && (
            <section className="card">
              <div className="card-head">
                <h3>Prior cases</h3>
                <span className="chip">{data.priorCases.length}</span>
              </div>
              <div className="card-body stack" style={{ gap: 'var(--s-sm)' }}>
                {data.priorCases.map((p) => (
                  <div key={p.id} className="report">
                    <div className="chip-row">
                      <span className="chip chip-mono">{p.reason}</span>
                      <span className="chip chip-indigo">{(p.verdict ?? 'no verdict').replace(/_/g, ' ')}</span>
                      <span className="eyebrow">
                        {formatTimestamp(p.closedAt)}
                        {p.assignedTo ? ` · ${p.assignedTo}` : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* ---------------- right: evidence, then the decision ---------------- */}
        <div className="stack">
          <section className="card">
            <div className="card-head">
              <h3>Automated triage</h3>
            </div>
            <div className="card-body">
              <AdvisoryBlock
                summary={data.triage.summary}
                suggestedVerdict={data.triage.suggestedVerdict}
                confidence={data.triage.confidence}
                source={data.triage.source}
              />
              {data.triage.disclaimer && (
                <p className="eyebrow" style={{ marginTop: 8 }}>
                  {data.triage.disclaimer}
                </p>
              )}
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h3>Reports</h3>
              <span className="chip">{data.reports.length}</span>
            </div>
            <div className="card-body">
              {data.reports.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>
                  No user report attached — this case was raised by an automated classifier, so the
                  message history below is the whole of the evidence.
                </p>
              ) : (
                data.reports.map((r) => (
                  <div key={r.id} className="report">
                    <div className="chip-row">
                      <span className="chip chip-mono">{r.reason}</span>
                      <span className="eyebrow">
                        {formatTimestamp(r.createdAt)} · reporter {shortId(r.reporterId)}
                      </span>
                      {r.messageId && <span className="chip chip-brass">points at a message</span>}
                    </div>
                    <p>{r.detail ?? 'No detail given by the reporter.'}</p>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h3>Recent messages</h3>
              <span className="chip">{data.messages.length} shown</span>
              <span className="chip chip-indigo">{subjectMessages.length} from the subject</span>
              <span className="eyebrow" style={{ marginLeft: 'auto' }}>
                this read is audited (§07)
              </span>
            </div>
            <div className="card-body">
              {data.messages.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>
                  No messages in any thread this account is part of. If the report is about openers,
                  the reply may have been blocked before it was ever sent.
                </p>
              ) : (
                <div className="transcript">
                  {[...data.messages].reverse().map((m) => (
                    <Message key={m.id} message={m} />
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="card" id="verdict">
            <div className="card-head">
              <h3>Verdict</h3>
              {closed && <span className="chip chip-indigo">already closed</span>}
            </div>
            <div className="card-body stack" style={{ gap: 'var(--s-md)' }}>
              {result && (
                <div className="notice is-good" role="status">
                  {result.message}
                </div>
              )}

              {closed && !result && (
                <div className="notice">
                  This case was closed as <b>{(k.verdict ?? 'unknown').replace(/_/g, ' ')}</b>
                  {k.closedAt ? ` on ${formatTimestamp(k.closedAt)}` : ''}. Recording a second
                  verdict on a closed case is not something this console does — open a new case
                  against the account instead.
                </div>
              )}

              {!closed && (
                <>
                  <div className="verdicts">
                    {MODERATION_VERDICTS.map((v) => {
                      const copy = VERDICT_COPY[v];
                      return (
                        <label
                          key={v}
                          className={`verdict-opt w-${copy.weight} ${verdict === v ? 'is-selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name="verdict"
                            value={v}
                            checked={verdict === v}
                            onChange={() => setVerdict(v)}
                            disabled={submitting}
                          />
                          <span>
                            <span className="label">{copy.label}</span>
                            <br />
                            <span className="what">{copy.what}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  <div className="field">
                    <label htmlFor="verdict-note">
                      Note <span style={{ color: 'var(--alta)' }}>· required</span>
                    </label>
                    <textarea
                      id="verdict-note"
                      className="textarea"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      maxLength={1000}
                      disabled={submitting}
                      placeholder="What you saw, and why this verdict. The next moderator to get this account reads this first."
                    />
                    <span className="hint">
                      {note.trim().length < MIN_NOTE
                        ? `At least ${MIN_NOTE} characters. A verdict without a reason is not a record.`
                        : `${note.trim().length}/1000`}
                    </span>
                  </div>

                  {verdict === 'banned' && (
                    <div className="notice is-warn">
                      A ban writes keys against device fingerprint, phone hash and every payment
                      instrument on record. It is not reversible from this console.
                    </div>
                  )}

                  {k.policyAction.preserveEvidence && (
                    <div className="notice">
                      This report category carries a legal hold — content is preserved rather than
                      deleted, whichever verdict you record (§08, §09).
                    </div>
                  )}

                  {submitError && (
                    <div className="notice is-bad" role="alert">
                      {submitError.message}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className={`btn ${verdict === 'banned' || verdict === 'suspended' ? 'btn-danger' : 'btn-primary'}`}
                      disabled={!verdict || note.trim().length < MIN_NOTE || submitting}
                      onClick={() => void submit()}
                    >
                      {submitting
                        ? 'Recording…'
                        : verdict
                          ? `Record ${VERDICT_COPY[verdict].label.toLowerCase()}`
                          : 'Choose a verdict'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => {
                        setVerdict(null);
                        setNote('');
                      }}
                      disabled={submitting || (!verdict && note.length === 0)}
                    >
                      Clear
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button type="button" className="btn btn-ghost btn-sm" onClick={onBack} style={{ marginBottom: 12 }}>
      ← Back to the queue
    </button>
  );
}

function Photo({ photo }: { photo: CasePhoto }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <figure className={`photo ${revealed ? '' : 'is-hidden'}`}>
      <img src={photo.url} alt={revealed ? `Photo in position ${photo.position}` : ''} loading="lazy" />
      {photo.removed && <span className="badge">removed</span>}
      <button type="button" className="reveal" onClick={() => setRevealed((r) => !r)}>
        {revealed ? 'Blur' : 'Reveal'}
      </button>
    </figure>
  );
}

function Message({ message }: { message: CaseMessage }) {
  return (
    <div
      className={`msg ${message.from === 'subject' ? 'from-subject' : 'from-other'} ${
        message.reported ? 'is-reported' : ''
      }`}
    >
      <div className="meta">
        <span>{message.from === 'subject' ? 'subject' : 'other party'}</span>
        <span>·</span>
        <span>{formatTimestamp(message.createdAt)}</span>
        {message.kind !== 'text' && <span className="chip chip-mono">{message.kind}</span>}
        {message.reported && <span className="chip chip-alta">reported</span>}
        <SafetyScoreChips scores={message.safetyScores} />
      </div>
      {message.redacted ? (
        <div className="redacted">
          Redacted. Removed under an evidence hold — the row is kept for the legal file, and the
          console is not where it gets re-read (§08).
        </div>
      ) : (
        <div className="body">{message.body ?? '(no text on this message)'}</div>
      )}
    </div>
  );
}
